/**
 * moduleIcons.js — Phase 2 App Shell (Jul 2026)
 *
 * Central lucide-react icon map for the canonical Phase 1 module catalog.
 * Every group in `config/moduleCatalog.js` (mirrored server-side) and every
 * module key gets a deterministic icon here so `AppSidebar` and
 * `CommandPalette` render identically without ad-hoc mapping.
 *
 * P0 (Feb 2026) — Taxonomy reorg to 10 groups / 44 modules. `expenses`
 * is a NEW group; `leave-balance` is a newly-cataloged module (route
 * /leave-balances already existed pre-reorg).
 *
 * Jul 2026 — Sidebar redesign: added `organization` group (Building2 icon)
 * for the renamed/reorganized sidebar sections.
 */

import {
  // Groups
  Home, Users, Clock, ScanFace, Settings, CalendarOff, Wallet, ShieldCheck, BarChart3, Receipt,
  Building2,
  // Modules
  LayoutDashboard, TrendingUp, Tv,
  Briefcase, UserPlus,
  Calendar, Tablet, Server, ClipboardList, CalendarHeart, AlertTriangle,
  UserSearch, Activity,
  Ticket, House as HouseIcon, CalendarDays, CalendarClock,
  DollarSign, Landmark, FileCheck,
  Shield,
  UtensilsCrossed, FileSpreadsheet,
  // Feb 2026 fork — Tea Toast P0 restore.
  Megaphone, TrendingDown, AlarmClock,
  // Fallback
  Circle,
} from 'lucide-react';

// ── Group icons ───────────────────────────────────────────────────────────────────────────────────
const GROUP_ICONS = {
  home:         Home,
  overview:     Home,
  people:       Users,
  attendance:   Clock,
  organization: Building2,
  selfie:       ScanFace,
  timeoff:      CalendarOff,
  expenses:     Receipt,
  payroll:      Wallet,
  compliance:   ShieldCheck,
  reports:      BarChart3,
  settings:     Settings,
};

// ── Module icons ──────────────────────────────────────────────────────────────────────────────
const MODULE_ICONS = {
  // Home
  'dashboard':                  LayoutDashboard,
  'analytics':                  TrendingUp,
  'tv-dashboard':               Tv,
  'announcements':              Megaphone,

  // People
  'employees':                  Users,
  'departments':                Building2,
  'contractors':                Briefcase,
  'visitor-management':         UserPlus,

  // Attendance
  'attendance':                 Calendar,
  'shifts':                     Clock,
  'kiosks':                     Tablet,
  'devices-recognition':        Server,
  'muster-log':                 ClipboardList,
  'holidays':                   CalendarHeart,
  'discipline-alerts':          AlertTriangle,
  'shift-compliance':           AlarmClock,

  // Selfie Audit
  'face-enrollment':            ScanFace,
  'face-verification-queue':    UserSearch,
  'face-recognition-dashboard': ScanFace,
  'face-analytics':             Activity,
  'hardware-devices':           Server,

  // Time Off
  'leaves':                     CalendarDays,
  'leave-balance':              CalendarClock,
  'wfh':                        HouseIcon,
  'comp-off':                   CalendarDays,
  'wfh-approvals':              CalendarDays,
  'comp-off-approvals':         CalendarDays,

  // Expenses
  'expenses':                   Receipt,
  'reimbursements':             Receipt,
  'expense-approvals':          Receipt,
  'reimbursement-approvals':    Receipt,

  // Payroll & Finance
  'payroll-v1':                 DollarSign,
  'payroll-v2':                 Landmark,
  'full-final-settlement':      FileCheck,
  'adhoc-deductions':           Receipt,

  // Compliance
  'audit-log':                  Shield,

  // Reports
  'reports':                    BarChart3,
  'attrition':                  TrendingDown,
  'ot-alerts':                  DollarSign,
  'canteen-report':             UtensilsCrossed,
  'amenities-report':           UtensilsCrossed,
  'additional-modules':         FileSpreadsheet,

  // Settings
  'settings':                   Settings,
  'tickets':                    Ticket,
};

export function iconForGroup(key) {
  return GROUP_ICONS[key] || Circle;
}

export function iconForModule(key) {
  return MODULE_ICONS[key] || Circle;
}

export default { iconForGroup, iconForModule };
